import { useState } from 'react';
import { useIsGuest, useMsal } from 'src/auth/firebaseMsalCompat';
import { useErrorBoundary } from 'react-error-boundary';
import axios from 'axios';

import { loginRequest } from '../../config/authConfig';
import { getApiHost } from '../../utils/getApiHost';
import { captureFunnelEvent } from '../../services/telemetry';

interface ImprovementSurveyData {
  selectedOption: string;
  selectedLabel: string;
  textInput?: string;
  dismissed: boolean;
}

interface WaitingSurveyData {
  surveyType: string;
  selectedOption: string;
  selectedLabel: string;
  dismissed: boolean;
}

export const useSurveySubmit = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { instance } = useMsal();
  const isGuest = useIsGuest();
  const { showBoundary } = useErrorBoundary();

  /**
   * Guest survey answers aren't persisted (no durable user to attach them to).
   * We still want the analytics signal, so we emit the same `survey_submitted`
   * event the backend would — from the browser, skipping the API round-trip.
   * Improvement "Other" free-text is included as `text_input` so guest
   * verbatims are visible in PostHog (they are not stored in Mongo). See
   * GUEST_UPLOAD_NEXT_PHASE.md for the planned full move to frontend-PostHog.
   */
  const captureGuestSurvey = (properties: {
    [key: string]: unknown;
  }): Promise<{ message: string; survey_id: null }> => {
    captureFunnelEvent('survey_submitted', { ...properties, is_guest: true });
    return Promise.resolve({ message: 'survey recorded', survey_id: null });
  };

  /**
   * Shared helper: acquire token, POST to the given path, and handle errors.
   * @param showBoundaryOnError - If false, errors are logged but not shown in the error boundary (for non-critical surveys).
   */
  const authenticatedPost = async (
    path: string,
    body: object,
    { showBoundaryOnError = true }: { showBoundaryOnError?: boolean } = {}
  ) => {
    try {
      setError(null);
      setLoading(true);

      const authResult = await instance.acquireTokenSilent({
        ...loginRequest,
      });

      const response = await axios.post(`${getApiHost()}${path}`, body, {
        headers: { Authorization: `Bearer ${authResult.idToken}` },
      });

      return response.data;
    } catch (err) {
      const asError = err instanceof Error ? err : new Error(String(err));
      setError(asError);
      console.error(`Error posting to ${path}:`, err);
      if (showBoundaryOnError) showBoundary(asError);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const submitImprovementSurvey = (
    projectId: string,
    data: ImprovementSurveyData
  ) => {
    if (isGuest) {
      return captureGuestSurvey({
        category: 'improvement',
        survey_type: 'improvement',
        selected_option: data.selectedOption,
        selected_label: data.selectedLabel,
        dismissed: data.dismissed,
        project_id: projectId,
        has_text_input: Boolean(data.textInput),
        text_input: data.textInput,
      });
    }
    return authenticatedPost(
      `/survey/improvement/${projectId}`,
      {
        selected_option: data.selectedOption,
        selected_label: data.selectedLabel,
        text_input: data.textInput,
        dismissed: data.dismissed,
      },
      // A survey answer must never be able to destroy the score the user came
      // for. This runs on completed score pages, so tripping the boundary here
      // replaced a finished project with the full-page crash screen — the same
      // customer-visible failure as the revoked-token bug, from an optional
      // question. `error` is still set and the throw still propagates.
      { showBoundaryOnError: false }
    );
  };

  const submitWaitingSurvey = (data: WaitingSurveyData) => {
    if (isGuest) {
      return captureGuestSurvey({
        category: 'waiting',
        survey_type: data.surveyType,
        selected_option: data.selectedOption,
        selected_label: data.selectedLabel,
        dismissed: data.dismissed,
      });
    }
    return authenticatedPost(
      '/survey/waiting',
      {
        survey_type: data.surveyType,
        selected_option: data.selectedOption,
        selected_label: data.selectedLabel,
        dismissed: data.dismissed,
      },
      { showBoundaryOnError: false }
    );
  };

  return {
    submitImprovementSurvey,
    submitWaitingSurvey,
    loading,
    error,
  };
};
